/** biome-ignore-all lint/style/useNamingConvention: allow snake case */
/* Global imports */
import cx from 'classnames';
import {FC, lazy, Suspense, useCallback, useEffect, useMemo, useState} from 'react';
import {createRoot} from 'react-dom/client';
import {ModalProvider} from 'react-modal-hook';
import {Provider} from 'react-redux';
import {Redirect, Route, RouteProps, Router, Switch, useLocation} from 'react-router-dom';
import {ToastContainer, toast} from 'react-toastify';
import {TransitionGroup} from 'react-transition-group';
import {ConfirmLeaveModalContainer} from '~/CreateFabricGreetingCard/navigation/ConfirmLeaveModal';
import {ErrorBoundary} from '~/common/components/ErrorBoundary/ErrorBoundary';
import {useAppTagging} from '~/common/components/ErrorBoundary/useAppTagging';
import {InjectableMegaNav} from '~/common/components/MegaNav/InjectableMegaNav';
import {DefaultNavHeader} from '~/common/components/ProgressHeader/DefaultNavHeader';
/* Local imports */
import {ProgressHeader} from '~/common/components/ProgressHeader/ProgressHeader';
import {INIT_APP} from '~/common/fabric/constants';
import {
  fetchContactsThunk,
  fetchGuestsImportThunk,
  fetchGuestsListThunk,
} from '~/common/fabric/EventGuests/api/guests';
import {repopulateContacts} from '~/common/fabric/EventGuests/Guests/GuestSearchSuggestions/useContactsWorker';
import {UPDATE_GUESTS} from '~/common/fabric/EventGuests/reducers/guests/constants';
import {
  INIT_PRODUCT_CONFIG_ID,
  LOAD_SELECTED_E_GIFT_CARD,
  STORE_E_GIFT_CARDS,
} from '~/common/fabric/GiftCard/reducers/eGiftCard';
import {useIsLargishAndUp} from '~/common/hooks/useMatchQuery';
import {InjectableFooter} from '~library/organisms/InjectableFooter';
import {getUserData} from '~library/utils/auth/utils';
import {
  useLazyGetAssetsQuery,
  useLazyGetCustomRecipientMessagesQuery,
  useLazyGetEcardQuery,
} from './api/ecard';
import {useLazyGetRetailersQuery} from './api/egift';
import {LoginModalReduxWrapper} from './components/LoginModalReduxWrapper/LoginModalReduxWrapper';
import {NavBottom} from './components/NavBottom/NavBottom';
import {EcardContextParent} from './context/EcardContext';
import {history} from './history';
import {useCuratedSets} from './hooks/useCuratedSets';
import {useCurrentRoute} from './hooks/useCurrentRoute';
import {useNavigateToRoute} from './hooks/useNavigateToRoute';
import {useTrackRouteChanges} from './hooks/useTrackRouteChanges';
import {store, useAppDispatch, useAppSelector, useAppStore} from './store';
import {updateAssets} from './store/reducers/assets';
import {
  loadEcard,
  loadFabricTemplate,
  selectEcardId,
  selectIsInEditMode,
  updateEventData,
  updateEventDesignOptions,
} from './store/reducers/ecard';
import {setSelectedRecipient, updateIndividualMessage} from './store/reducers/recipientMessages';
import {CuratedCard} from './types/curatedCard';
import {Assets} from './types/ecard';
import {Step} from './types/misc';
import {getEcardIdFromURL, handleRetrievedCardData} from './utils/ecard';
import {numSteps, steps} from './utils/navigation';
import {trackRecipientPageview} from './utils/tracking';

import 'react-toastify/dist/ReactToastify.min.css';
import './app.sass';

// Step 1: Design
const QuickEcardDesignContainer = lazy(() =>
  import('~/QuickEcards/routes/QuickEcardDesign/QuickEcardDesign').then((module) => ({
    default: module.QuickEcardDesignContainer,
  }))
);

// Step 2: eGift Cards
const GiftCards = lazy(() =>
  import('~/QuickEcards/routes/GiftCards/GiftCards').then((module) => ({
    default: module.GiftCards,
  }))
);

// Step 3: Add Recipients
const Recipients = lazy(() =>
  import('~/QuickEcards/routes/Recipients/Recipients').then((module) => ({
    default: module.Recipients,
  }))
);

// Step 4: Recipient Messages
const RecipientMessages = lazy(() =>
  import('~/QuickEcards/routes/RecipientMessages/RecipientMessages').then((module) => ({
    default: module.RecipientMessages,
  }))
);

// Step 5: Review
const Review = lazy(() =>
  import('~/QuickEcards/routes/Review/Review').then((module) => ({
    default: module.Review,
  }))
);

// Create Success
const CreateSuccess = lazy(() =>
  import('~/QuickEcards/routes/Success/Success').then((module) => ({
    default: module.Success,
  }))
);

// Recipient View
const ViewEcard = lazy(() =>
  import('~/QuickEcards/routes/ViewEcard/ViewEcard').then((module) => ({
    default: module.ViewEcard,
  }))
);

// SMS Verification (for eGift Cards)
const SmsVerification = lazy(() =>
  import('~/QuickEcards/routes/SmsVerification/SmsVerification').then((module) => ({
    default: module.SmsVerification,
  }))
);

// Redeem eGift Card
const Redemption = lazy(() =>
  import('~/QuickEcards/routes/Redemption/Redemption').then((module) => ({
    default: module.Redemption,
  }))
);

// Reaction Success
const ViewEcardSuccess = lazy(() =>
  import('~/QuickEcards/routes/ViewEcardSuccess/ViewEcardSuccess').then((module) => ({
    default: module.ViewEcardSuccess,
  }))
);

store.dispatch({
  type: INIT_APP,
  payload: {
    user: getUserData(),
  },
});

store.dispatch({
  type: UPDATE_GUESTS,
  payload: {
    fabricGreetingCard: true,
  },
});

let createAppMounted = false;
let viewAppMounted = false;

const QuickEcardsCreateApp: FC = () => {
  const {currentRoute, isUnsavedCreateStep} = useCurrentRoute();
  useTrackRouteChanges({currentRoute});

  const isInEditMode = useAppSelector(selectIsInEditMode);
  const eCardId = useAppSelector(selectEcardId);
  const store = useAppStore();
  const dispatch = useAppDispatch();
  const [getEcard] = useLazyGetEcardQuery();
  const [getAssets] = useLazyGetAssetsQuery();
  const {getCuratedSets} = useCuratedSets();
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [getGiftCardRetailers] = useLazyGetRetailersQuery();
  const navigateToRoute = useNavigateToRoute();
  const isDesktop = useIsLargishAndUp();

  useEffect(() => {
    if (createAppMounted) return;
    createAppMounted = true;

    async function startFetching() {
      const searchParams = new URLSearchParams(window.location.search);
      const c = searchParams.get('c') ?? '';
      const category = c || window.category_event_type || window.fabricTemplate.event_type;
      dispatch(loadFabricTemplate(window.fabricTemplate));

      let assets: Partial<Assets> = {};
      if (isUnsavedCreateStep) {
        const {tracking_product_type: trackingProductType} = window;
        // Save default analytics info
        dispatch(
          loadEcard({
            analytics: {
              tracking_product_type: trackingProductType,
              tracking_data: {gallery_category: category},
            },
          })
        );

        // Save event type
        dispatch(
          updateEventData({
            event_type: category,
          })
        );

        // Get reveals and effects
        const res = await getAssets({
          assetGroups: window.fabricTemplate.asset_groups,
          primaryCategories: window.fabricTemplate.primary_categories,
          templateId: window.fabricTemplate.id,
        });
        assets = res.data ?? {};

        // Get curated sets
        const {data: curatedCards} = await getCuratedSets({
          category_id: category,
          page_offset: 1,
          page_size: 40,
        });
        const foundInitialTemplate = curatedCards?.find(
          (card) => 'matching_set' in card && card.id === window.fabricTemplate.id
        ) as CuratedCard | undefined;

        // Get background for template
        const foundBackgroundInAsset = assets.backgrounds?.find(
          (bg) => foundInitialTemplate?.backgrounds[0] === bg.id
        );

        const backgroundObj = {
          background:
            foundBackgroundInAsset?.media[2].url ?? foundInitialTemplate?.background_color,
          background_image_desktop: foundBackgroundInAsset?.media[1].url ?? '',
          background_image_mobile: foundBackgroundInAsset?.media[2].url ?? '',
        };

        dispatch(
          updateEventDesignOptions({
            ...backgroundObj,
            ...(foundInitialTemplate?.matching_set.effects ?? {}),
            ...(foundInitialTemplate?.matching_set.reveals ?? {}),
          })
        );

        if (window.fabricTemplate.create_flow_config?.egift_cards_enabled) {
          // Initialize selected eGift Card
          const params = new URL(document.location.href).searchParams;
          const urlProductConfigId = params.get('product_config_id');
          if (urlProductConfigId) {
            dispatch({
              type: INIT_PRODUCT_CONFIG_ID,
              payload: urlProductConfigId,
            });
          }

          const {data: giftCardRetailers} = await getGiftCardRetailers();
          dispatch({type: STORE_E_GIFT_CARDS, payload: giftCardRetailers ?? []});
        }
      } else {
        // Load saved eCard
        const id = getEcardIdFromURL();
        const {data} = await getEcard(id);
        if (!data) return;
        handleRetrievedCardData(data, store);
        dispatch(
          loadEcard({
            ...data,
            id,
          })
        );

        // Load EGift Card state
        dispatch({
          type: LOAD_SELECTED_E_GIFT_CARD,
          payload: data.event_data.gift_card_data,
        });

        // Get reveals and effects
        const res = await getAssets({
          assetGroups: window.fabricTemplate.asset_groups,
          primaryCategories: window.fabricTemplate.primary_categories,
          templateId: window.fabricTemplate.id,
        });
        assets = res.data ?? {};

        // Get curated sets
        await getCuratedSets({
          category_id: data.event_data.event_type,
          page_offset: 1,
          page_size: 40,
        });

        if (
          data.event_data.gift_card_data.product_config_id ||
          window.fabricTemplate.create_flow_config?.egift_cards_enabled
        ) {
          const {data: giftCardRetailers} = await getGiftCardRetailers();
          dispatch({type: STORE_E_GIFT_CARDS, payload: giftCardRetailers ?? []});
        }

        const fetchGuests = dispatch(fetchGuestsListThunk());
        const fetchContacts = dispatch(fetchContactsThunk());
        const fetchGuestsImport = dispatch(fetchGuestsImportThunk());
        Promise.all([fetchGuests, fetchContacts, fetchGuestsImport]).finally(() => {
          window.setTimeout(() => repopulateContacts(id), 400);
        });
      }
      dispatch(updateAssets(assets));
    }

    startFetching();
  }, [
    dispatch,
    store,
    isUnsavedCreateStep,
    getEcard,
    getAssets,
    getCuratedSets,
    getGiftCardRetailers,
  ]);

  useEffect(() => {
    document.body.classList.add('no-overflow');
  }, []);

  const currentRouteId = currentRoute?.id ?? 1;

  const RedirectToCardGallery = useCallback(() => <Redirect to="/" />, []);

  const routes: (RouteProps & {key: string})[] = useMemo(
    () => [
      {
        exact: true,
        path: ['/:template/create', `/:eventId/${steps.customize.path}`],
        component: QuickEcardDesignContainer,
        key: steps.customize.path,
      },
      {
        exact: true,
        path: [`/:eventId/${steps['egift-card'].path}`],
        component: GiftCards,
        key: steps['egift-card'].path,
      },
      {
        exact: true,
        path: [`/:eventId/${steps.recipients.path}`],
        component: Recipients,
        key: steps.recipients.path,
      },
      {
        exact: true,
        path: [`/:eventId/${steps['recipients-message'].path}`],
        component: RecipientMessages,
        key: steps['recipients-message'].path,
      },
      {
        exact: true,
        path: [`/:eventId/${steps.review.path}`],
        component: Review,
        key: steps.review.path,
      },
      {
        exact: true,
        path: [`/:eventId/${steps.success.path}`],
        component: CreateSuccess,
        key: steps.success.path,
      },
    ],
    []
  );

  return (
    <div
      className={cx('quick-ecards__create-wrapper', {
        'quick-ecards__create-wrapper--overflow': currentRoute?.showFooter,
      })}
    >
      {currentRoute?.showMegaNav ? <InjectableMegaNav /> : null}
      {!isInEditMode && currentRoute && currentRoute.id < steps[Step.SUCCESS].id ? (
        <ProgressHeader
          navElements={
            <DefaultNavHeader
              currentRoute={currentRoute}
              totalSteps={numSteps}
              handleCloseButtonClick={() => setShowLeaveModal(true)}
              isPremium
              ctaPortal={currentRoute.headerCtas && isDesktop}
            />
          }
          showPreviewNav={false}
          showProgressBar
          totalSteps={numSteps}
          currentStepId={currentRouteId}
        />
      ) : null}
      <div className={cx('root-page-wrapper', currentRoute?.path)} id="root-page-wrapper">
        <div className="quick-ecards__page" id={currentRoute?.path}>
          <Suspense fallback={null}>
            <Switch>
              {routes.map(({key, ...route}) => (
                <Route key={key} {...route} />
              ))}
              <Route
                exact
                path={['/:template/*', '/:eventId/*']}
                component={RedirectToCardGallery}
              />
            </Switch>
          </Suspense>
        </div>
      </div>
      {showLeaveModal && (
        <ConfirmLeaveModalContainer
          isFirstStep={!eCardId}
          isEditingFlow={!!currentRoute?.meta?.editing}
          leave={async () => {
            setShowLeaveModal(false);
            await navigateToRoute(Step.EXIT, store, !eCardId);
          }}
          cancelLeave={() => setShowLeaveModal(false)}
        />
      )}
      {currentRoute?.overrideBottomNav ? <NavBottom /> : null}
      {currentRoute?.showFooter ? <InjectableFooter /> : null}
    </div>
  );
};

const QuickEcardsViewApp: FC = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const ecardId = getEcardIdFromURL();
  const [getAssets] = useLazyGetAssetsQuery();
  const [getCustomRecipientMessages] = useLazyGetCustomRecipientMessagesQuery();
  const ready = !!useAppSelector(selectEcardId);

  useAppTagging({
    ecardId,
    eventId: window.evite_event?.id,
  });

  useEffect(() => {
    if (viewAppMounted) return;
    viewAppMounted = true;

    async function startFetching() {
      dispatch(loadFabricTemplate(window.fabricTemplate));
      if (window.evite_event) {
        // Load window evite event object into Redux store
        dispatch(
          loadEcard({
            id: window.evite_event.id,
            event_data: {
              event_host_name: window.evite_event.host_name,
              event_type: window.evite_event.event_type,
              used_as: window.evite_event.used_as,
              known_timezone_name: window.evite_event.known_timezone_name,
              status: window.evite_event.status,
              system_generated: window.evite_event.system_generated,
              user_saved: window.evite_event.user_saved,
              is_pro: window.evite_event.is_pro,
              options: {
                design: window.evite_event.options.design,
                event_option_disable_rokt: window.evite_event.options.event_option_disable_rokt,
                write: window.evite_event.options.write,
              },
              fabric_design_data: window.evite_event.fabric_design_data,
              gift_card_data: window.evite_event.gift_card_data,
            },
            sender_data: {
              host_id: window.evite_event.host_id,
            },
            template_data: {
              template_name: window.evite_event.template_name,
              title: window.evite_event.title,
            },
            is_ecard: true,
            analytics: {
              tracking_data: window.evite_event.tracking_data,
              tracking_product_type: window.tracking_product_type,
            },
          })
        );

        // Load EGift Card state
        dispatch({
          type: LOAD_SELECTED_E_GIFT_CARD,
          payload: window.evite_event.gift_card_data,
        });
      }

      // Get assets
      const {data: assetsData} = await getAssets({
        assetGroups: window.fabricTemplate.asset_groups,
        primaryCategories: window.fabricTemplate.primary_categories,
        templateId: window.fabricTemplate.id,
      });
      dispatch(updateAssets(assetsData ?? {}));

      // Get recipient messages
      const {data: messageData} = await getCustomRecipientMessages(ecardId);
      if (messageData && Object.keys(messageData).length > 0) {
        dispatch(updateIndividualMessage(messageData));
      }

      // Update selected guest
      dispatch(setSelectedRecipient(window?.guest_id || null));
    }

    startFetching();
  }, [dispatch, ecardId, getAssets, getCustomRecipientMessages]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    if (!viewAppMounted) return;
    trackRecipientPageview();
  }, [location.pathname]);

  useEffect(() => {
    if (ready) {
      evite.when('nav').finally(() => {
        setTimeout(() => {
          const page = document.getElementById('main_content');
          page?.scrollIntoView();
        }, 200);
      });
    }
  }, [ready]);

  return (
    <Suspense fallback={null}>
      <Switch>
        <Route exact path={['/']} component={ViewEcard} />
        <Route exact path={['/sms-verification']} component={SmsVerification} />
        <Route exact path={['/redeem']} component={Redemption} />
        <Route exact path={['/success']} component={ViewEcardSuccess} />
      </Switch>
    </Suspense>
  );
};

const QuickEcardsAppWrapper = () => {
  const isCreateFlow = window.location.pathname.split('/')[1] === 'cards';

  return (
    <ErrorBoundary componentName="QuickEcardsViewApp">
      <Provider store={store}>
        <ModalProvider rootComponent={TransitionGroup}>
          <EcardContextParent>
            <Router history={history}>
              <LoginModalReduxWrapper />
              {isCreateFlow ? <QuickEcardsCreateApp /> : <QuickEcardsViewApp />}
            </Router>
          </EcardContextParent>

          <ToastContainer
            position="bottom-right"
            autoClose={6000}
            hideProgressBar
            closeOnClick
            draggable
            icon={false}
          />
        </ModalProvider>
      </Provider>
    </ErrorBoundary>
  );
};
window.toast = toast;

const root = createRoot(document.getElementById('quick-ecards-root')!);
root.render(<QuickEcardsAppWrapper />);
